// PHARMACY REQUESTS — the ward asking the IP pharmacy to supply this patient's
// active medication orders (multi-store Phase 4; design doc §8.4).
//
// Supplying is not administering. A request moves medicine from the IP
// pharmacy to the ward; it says nothing about whether a dose was given — that
// is still charted, dose by dose, in the medication chart above. This panel
// therefore never shows a dose state, only what was asked for and supplied.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { toast } from '@/hooks/useToast';
import { useAdmissionRequests, useCancelWardRequest, useCreateWardRequest } from '@/features/pharmacy/wardRequests/hooks';
import { PRIORITY_OPTIONS, PriorityBadge, WardRequestStatusBadge } from '@/features/pharmacy/wardRequests/ui';
import type { WardRequest, WardRequestPriority } from '@/features/pharmacy/wardRequests/api';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import type { MedicationOrder } from '../types';
import { Section, Empty, fmtDateTime, useCan } from '../ui';

export function WardRequestsPanel({ admissionId, orders, readOnly }: {
  admissionId: number;
  /** This admission's orders; only active/held ones can be requested. */
  orders: MedicationOrder[];
  readOnly: boolean;
}) {
  const can = useCan();
  const canRequest = can('ip:med-request') && !readOnly;
  const list = useAdmissionRequests(admissionId);
  const create = useCreateWardRequest(admissionId);
  const cancel = useCancelWardRequest();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState<Record<number, string>>({});
  const [priority, setPriority] = useState<WardRequestPriority>('routine');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState<WardRequest | null>(null);
  const requestable = orders.filter((o) => ['active', 'held'].includes(o.status));

  const submit = () => {
    setError('');
    const items = requestable
      .filter((o) => (qty[o.id] ?? '').trim() !== '')
      .map((o) => ({ medication_order_id: o.id, quantity: Number(qty[o.id]) }));
    if (!items.length) return setError('Enter a quantity for at least one order.');
    if (items.some((i) => !Number.isInteger(i.quantity) || i.quantity <= 0)) return setError('Quantities must be whole numbers above zero.');
    create.mutate({ priority, notes: notes.trim() || undefined, items }, {
      onSuccess: (r) => {
        toast({ title: `${r.request_number} sent to the IP pharmacy`, variant: 'success' });
        setOpen(false); setQty({}); setNotes(''); setPriority('routine');
      },
      onError: (e) => setError(e instanceof Error ? e.message : 'Could not send the request'),
    });
  };

  const requests = list.data ?? [];

  return (
    <Section
      title="Pharmacy requests"
      subtitle="Ask the IP pharmacy to send this patient’s medicines to the ward. Supplying a medicine is not giving a dose — chart each dose in the medication chart."
      action={canRequest && !open && requestable.length > 0 && (
        <Button size="sm" onClick={() => setOpen(true)}>+ Request from pharmacy</Button>
      )}
    >
      {open && (
        <div className="mb-4 space-y-3 rounded-lg border border-border p-3">
          <div className="text-[12.5px] text-ink-muted">Quantity is in units the pharmacy hands over — tablets, vials, ampoules.</div>
          <div className="divide-y divide-border rounded-md border border-border">
            {requestable.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1 text-[13px]">
                  <span className="font-semibold">{o.drug_name}{o.strength ? ` ${o.strength}` : ''}</span>
                  <span className="block text-[11.5px] text-ink-muted">
                    {[o.dose_quantity && `${o.dose_quantity} ${o.dose_unit ?? ''}`.trim(), o.route, o.frequency_code].filter(Boolean).join(' · ')}
                    {o.status === 'held' ? ' · on hold' : ''}
                  </span>
                </div>
                <Input type="number" min={1} inputMode="numeric" className="w-24" placeholder="Qty"
                  aria-label={`Quantity of ${o.drug_name}`}
                  value={qty[o.id] ?? ''} onChange={(e) => setQty((q) => ({ ...q, [o.id]: e.target.value }))} />
              </div>
            ))}
          </div>
          <SegmentedControl options={PRIORITY_OPTIONS} value={priority} onChange={setPriority} size="sm" className="w-[260px]" />
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the pharmacy (optional)" />
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{error}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setError(''); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} onClick={submit}>Send to pharmacy</Button>
          </div>
        </div>
      )}

      {!requests.length ? (
        <Empty label="No pharmacy requests for this admission" hint={canRequest && requestable.length ? 'Use “+ Request from pharmacy”.' : undefined} />
      ) : (
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="rounded-lg border border-border px-3 py-2 text-[13px]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[12px] font-semibold">{r.request_number}</span>
                <WardRequestStatusBadge status={r.status} />
                <PriorityBadge priority={r.priority} />
                <span className="flex-1" />
                {canRequest && r.status === 'requested' && (
                  <Button variant="ghost" size="sm" onClick={() => { cancel.reset(); setCancelling(r); }}>Cancel</Button>
                )}
              </div>
              <ul className="mt-1 space-y-0.5 text-[12.5px]">
                {r.items.map((it) => (
                  <li key={it.id}>
                    {it.drug_name} — asked {it.qty_requested}
                    {r.status !== 'requested' && r.status !== 'cancelled' && <>, sent {it.qty_dispensed}</>}
                    {it.short_reason && <span className="text-warn-ink"> ({it.short_reason})</span>}
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[11.5px] text-ink-muted">
                Asked {fmtDateTime(r.requested_at)} · {r.requested_by}
                {r.dispensed_at && <> · sent {fmtDateTime(r.dispensed_at)}{r.bill_number ? ` on ${r.bill_number}` : ''}</>}
                {r.collected_at && <> · handed to {r.collected_by} {fmtDateTime(r.collected_at)}</>}
                {r.cancelled_at && <> · cancelled: {r.cancel_reason}</>}
              </div>
            </div>
          ))}
        </div>
      )}
      <TextPromptDialog
        open={!!cancelling}
        title={`Cancel ${cancelling?.request_number ?? ''}?`}
        label="Why cancel this request?"
        placeholder="e.g. Raised twice, or order changed"
        confirmLabel="Cancel request"
        danger
        pending={cancel.isPending}
        error={cancel.error instanceof Error ? cancel.error.message : null}
        onConfirm={(reason) => {
          if (!cancelling) return;
          cancel.mutate({ id: cancelling.id, reason }, {
            onSuccess: (r) => { toast({ title: `${r.request_number} cancelled`, variant: 'success' }); setCancelling(null); },
          });
        }}
        onClose={() => setCancelling(null)}
      />
    </Section>
  );
}
